// src/components/Nadlan.jsx //

import React, { useState, useEffect, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { db, storage } from '../firebase'; 
import { collection, addDoc, onSnapshot, doc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import LoadingAnimation from './LoadingAnimation';
import { getAuth } from "firebase/auth";

// --- Icon Components (for Property Card & Detail) ---
const BedIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" /></svg>;
const BathIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5zM12 11.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5zM12 16.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5zM12 21.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" /></svg>;
const RulerIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" /></svg>;
const ElevatorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8l4 4m-4-4l-4 4m4-4v8" />
    </svg>
);
const ParkingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h2a3 3 0 010 6H9V8z" />
    </svg>
);
const BalconyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M3 12h18M3 18h18" /></svg>;
const MamadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
const AirConIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6l4 4m-4-4l-4 4m4-4v12M4 12h16" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4l2 2M4 7l2 2m11-5l-2 2m5-2l-2 2" />
    </svg>
);
const DiamondIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 3h12l4 6-10 12L2 9l4-6z M2 9h20 M6 3l4 6h4l4-6 M12 21V9" />
    </svg>
);
const ExclusiveIcon = DiamondIcon;
const StorageIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 3H4a2 2 0 00-2 2v14a2 2 0 002 2h16a2 2 0 002-2V5a2 2 0 00-2-2zM4 7h16M4 11h16M4 15h16" /></svg>;

// אייקונים לאנימציית טעינה
const HouseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
);

const CottageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3M9 21h6M8 6h8m-8 6h8" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16h4m8 0h4" />
    </svg>
);

// Add new icon components for sharing
const WhatsAppIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

// --- Main Nadlan Component (Modified) ---
export default function Nadlan({ effectiveUserId, isEditMode }) {
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [propertyToEdit, setPropertyToEdit] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isOpeningForm, setIsOpeningForm] = useState(false);
    const [isClosingForm, setIsClosingForm] = useState(false);
    const [isDeletingProperty, setIsDeletingProperty] = useState(false);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [filters, setFilters] = useState({
        priceRange: { min: '', max: '' },
        rooms: '',
        propertyType: '',
        sizeRange: { min: '', max: '' },
        city: '',
        features: {
            parking: false,
            elevator: false,
            mamad: false,
            storage: false
        },
        floor: '',
        balcony: '',
        toilets: '',
        bathrooms: '',
        airConditioning: '',
        exclusivity: '',
        searchText: '',
        neighborhood: ''
    });

    // Add the filtered properties calculation
    const filteredProperties = useMemo(() => {
        return properties.filter(property => {
            // Price Range Filter – cast to number for safe comparison (remove commas first)
            const propertyPrice = Number(property.price?.toString().replace(/,/g, ''));
            const minPrice = Number(filters.priceRange.min?.toString().replace(/,/g, ''));
            const maxPrice = Number(filters.priceRange.max?.toString().replace(/,/g, ''));
            
            if (filters.priceRange.min && propertyPrice < minPrice) return false;
            if (filters.priceRange.max && propertyPrice > maxPrice) return false;

            // Rooms Filter – convert both sides to number for reliable comparison
            if (filters.rooms && Number(property.rooms) !== Number(filters.rooms)) return false;

            // Property Type Filter
            if (filters.propertyType && property.propertyType !== filters.propertyType) return false;

            // Size Range Filter
            if (filters.sizeRange.min && Number(property.size) < Number(filters.sizeRange.min)) return false;
            if (filters.sizeRange.max && Number(property.size) > Number(filters.sizeRange.max)) return false;

            // City Filter
            if (filters.city && !property.address?.city?.toLowerCase().includes(filters.city.toLowerCase())) return false;

            // Features Filter
            if (filters.features.parking && property.features.parking === 0) return false;
            if (filters.features.elevator && !property.features.elevator) return false;
            if (filters.features.mamad && !property.features.mamad) return false;
            if (filters.features.storage && !property.features.storage) return false;

            // Floor Filter
            if (filters.floor) {
                if (filters.floor === 'מרתף') {
                    if (property.floor !== 'מרתף') return false;
                } else {
                    if (Number(property.floor) !== Number(filters.floor)) return false;
                }
            }

            // Balcony Filter
            if (filters.balcony !== '' && Number(property.balcony) !== Number(filters.balcony)) return false;

            // Toilets Filter
            if (filters.toilets !== '' && Number(property.toilets) !== Number(filters.toilets)) return false;

            // Bathrooms Filter
            if (filters.bathrooms !== '' && Number(property.bathrooms) !== Number(filters.bathrooms)) return false;

            // Air Conditioning Filter
            if (filters.airConditioning && property.airConditioning !== filters.airConditioning) return false;

            // Exclusivity Filter
            if (filters.exclusivity && property.exclusivity !== filters.exclusivity) return false;

            // Free text search in notes
            if (filters.searchText && typeof property.notes === 'string') {
                const term = filters.searchText.trim().toLowerCase();
                if (term && !property.notes.toLowerCase().includes(term)) return false;
            }
            if (filters.searchText && typeof property.notes !== 'string') return false;

            return true;
        });
    }, [properties, filters]);

    // בדיקת פרמטר ה-URL בטעינה
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const propertyId = urlParams.get('propertyId');
        
        if (propertyId && properties.length > 0) {
            const property = properties.find(p => p.id === propertyId);
            if (property) {
                setSelectedProperty(property);
            }
        }
    }, [properties]);

    useEffect(() => {
        if (!effectiveUserId) return;

const auth = getAuth();
console.log("Current Firebase User:", auth.currentUser);

        const propertiesCollectionRef = collection(db, 'users', effectiveUserId, 'properties');
        const unsubscribe = onSnapshot(propertiesCollectionRef, (snapshot) => {
            const propsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProperties(propsList);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching properties: ", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [effectiveUserId]);

    const handleAddClick = async () => {
        setIsOpeningForm(true);
        // Add a longer delay to allow the loading animation to show and switch icons
        await new Promise(resolve => setTimeout(resolve, 1000));
        setPropertyToEdit(null);
        setIsFormOpen(true);
        setIsOpeningForm(false);
    };

    const handleEditClick = async (prop) => {
        setIsOpeningForm(true);
        // Add a longer delay to allow the loading animation to show and switch icons
        await new Promise(resolve => setTimeout(resolve, 1000));
        setPropertyToEdit(prop);
        setIsFormOpen(true);
        setIsOpeningForm(false);
    };

    const handleDeleteClick = async (prop) => {
        if (window.confirm('האם אתה בטוח שברצונך למחוק את הנכס? פעולה זו אינה הפיכה.')) {
            try {
                setIsDeletingProperty(true);
                if (prop.media && prop.media.length > 0) {
                    for (const item of prop.media) {
                        const fileRef = ref(storage, item.path);
                        await deleteObject(fileRef);
                    }
                }
                await deleteDoc(doc(db, 'users', effectiveUserId, 'properties', prop.id));
                if (selectedProperty && selectedProperty.id === prop.id) {
                    setSelectedProperty(null);
                }
            } catch (error) {
                console.error("Error deleting property: ", error);
                alert('שגיאה במחיקת הנכס.');
            } finally {
                setIsDeletingProperty(false);
            }
        }
    };

    const handleFormSave = async (formData) => {
        try {
            setIsClosingForm(true);
            // Make sure we have a media array, even if empty
            const dataToSave = {
                ...formData,
                media: formData.media || [],
                createdAt: propertyToEdit ? propertyToEdit.createdAt : Timestamp.now()
            };

            if (propertyToEdit) {
                const propDocRef = doc(db, 'users', effectiveUserId, 'properties', propertyToEdit.id);
                await updateDoc(propDocRef, dataToSave);
            } else {
                const propertiesCollectionRef = collection(db, 'users', effectiveUserId, 'properties');
                await addDoc(propertiesCollectionRef, dataToSave);
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsFormOpen(false);
            setPropertyToEdit(null);
            setIsClosingForm(false);
        } catch (error) {
            console.error("Error saving property: ", error);
            alert('שגיאה בשמירת הנכס.');
            setIsClosingForm(false);
        }
    };

    // בדיקה האם יש סינון פעיל
    const hasActiveFilters = useMemo(() => {
        return filters.priceRange.min !== '' ||
            filters.priceRange.max !== '' ||
            filters.rooms !== '' ||
            filters.floor !== '' ||
            filters.propertyType !== '' ||
            filters.balcony !== '' ||
            filters.toilets !== '' ||
            filters.bathrooms !== '' ||
            filters.airConditioning !== '' ||
            filters.exclusivity !== '' ||
            filters.features.parking ||
            filters.features.elevator ||
            filters.features.mamad ||
            filters.sizeRange.min !== '' ||
            filters.sizeRange.max !== '' ||
            filters.city !== '' ||
            filters.searchText !== '' ||
            filters.neighborhood !== '';
    }, [filters]);

    // פונקציה לאיפוס כל הסינונים
    const resetAllFilters = () => {
        setFilters({
            priceRange: { min: '', max: '' },
            rooms: '',
            floor: '',
            propertyType: '',
            balcony: '',
            toilets: '',
            bathrooms: '',
            airConditioning: '',
            exclusivity: '',
            features: {
                parking: false,
                elevator: false,
                mamad: false,
                storage: false
            },
            sizeRange: { min: '', max: '' },
            city: '',
            searchText: '',
            neighborhood: ''
        });
    };

    const handleFormClose = async () => {
        if (!isClosingForm) {  // Prevent multiple closes
            setIsClosingForm(true);
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsFormOpen(false);
            setPropertyToEdit(null);
            setIsClosingForm(false);
        }
    };

    const handleViewProperty = (property) => {
        setSelectedProperty(property);
        // עדכון ה-URL עם ID הנכס
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('propertyId', property.id);
        window.history.pushState({}, '', newUrl);
    };

    // ניקוי פרמטר ה-URL כשחוזרים לרשימה
    const handleReturnToList = () => {
        setSelectedProperty(null);
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('propertyId');
        window.history.pushState({}, '', newUrl);
    };

    // --- שינוי מרכזי: החלפת כל מבנה התצוגה ---
    return (
        <div className="container mx-auto px-4 py-8">
            {selectedProperty ? (
                <div className="mb-4">
                    <button
                        onClick={handleReturnToList}
                        className="fixed bottom-6 right-6 z-50 bg-gray-800 hover:bg-gray-700 text-amber-400 w-16 h-16 rounded-full shadow-lg flex flex-col items-center justify-center text-sm font-medium transition-all hover:scale-105 border border-amber-400/30"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <span>לכל</span>
                        <span>התוצאות</span>
                    </button>

                    <div className="space-y-6">
                        {/* פרטי הנכס - עכשיו בראש הדף */}
                        <div className="bg-gray-800 rounded-lg shadow-lg">
                            <PropertyDetails property={selectedProperty} />
                            
                            {/* כפתורי שיתוף */}
                            <div className="p-4 border-t border-gray-700">
                                <div className="flex justify-center gap-4">
                                    <button
                                        onClick={() => setIsShareDialogOpen(true)}
                                        className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <WhatsAppIcon />
                                        <span>שתף בוואטסאפ</span>
                                    </button>
                                    <button
                                        onClick={() => setIsShareDialogOpen(true)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <FacebookIcon />
                                        <span>שתף בפייסבוק</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* תמונות בסידור אנכי */}
                        <div className="space-y-4">
                            {selectedProperty.media?.filter(m => m.type.startsWith('image')).map((media, index) => (
                                <div key={index} className="w-full">
                                    <img 
                                        src={media.url} 
                                        alt={`תמונה ${index + 1}`}
                                        className="w-full h-auto rounded-lg shadow-lg"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* סרטונים */}
                        {selectedProperty.media?.filter(m => m.type.startsWith('video')).length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold text-white">סרטונים</h3>
                                {selectedProperty.media?.filter(m => m.type.startsWith('video')).map((media, index) => (
                                    <div key={index} className="w-full">
                                        <video 
                                            src={media.url} 
                                            controls 
                                            className="w-full rounded-lg shadow-lg"
                                            preload="metadata"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {isLoading && <LoadingAnimation message="טוען נכסים..." />}
                    {isDeletingProperty && <LoadingAnimation message="מוחק נכס..." />}
                    {isOpeningForm && <LoadingAnimation message="טוען טופס..." />}
                    {isClosingForm && <LoadingAnimation message={propertyToEdit ? "שומר שינויים..." : "שומר נכס חדש..."} />}
                    {isEditMode && (
                        <div className="text-center mb-6">
                            <button onClick={handleAddClick} className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 px-6 rounded-lg transition-colors">
                                + הוסף נכס
                            </button>
                        </div>
                    )}

                    {!isEditMode && (
                        <>
                            <h2 className="text-3xl font-bold text-center text-white mb-6">נכסים על המדף</h2>
                            <FilterComponent filters={filters} setFilters={setFilters} />
                        </>
                    )}
                    <div className="grid grid-cols-1 gap-6">
                        {filteredProperties.map(property => (
                            <PropertyCard
                                key={property.id}
                                property={property}
                                isEditMode={isEditMode}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteClick}
                                onView={() => handleViewProperty(property)}
                                effectiveUserId={effectiveUserId}
                            />
                        ))}
                    </div>

                    {/* כפתור איפוס סינונים צף */}
                    {hasActiveFilters && !selectedProperty && (
                        <button 
                            onClick={resetAllFilters}
                            className="fixed bottom-6 right-6 z-50 bg-gray-800 hover:bg-gray-700 text-amber-400 w-16 h-16 rounded-full shadow-lg flex flex-col items-center justify-center text-sm font-medium transition-all hover:scale-105 border border-amber-400/30"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>לכל</span>
                            <span>הנכסים</span>
                        </button>
                    )}
                </>
            )}

            {isFormOpen && (
                <PropertyForm 
                    isOpen={isFormOpen}
                    onClose={handleFormClose}
                    onSave={handleFormSave}
                    initialData={propertyToEdit}
                    effectiveUserId={effectiveUserId}
                />
            )}

            {/* Share Preview Dialog */}
            {selectedProperty && (
                <SharePreviewDialog
                    isOpen={isShareDialogOpen}
                    onClose={() => setIsShareDialogOpen(false)}
                    property={selectedProperty}
                    effectiveUserId={effectiveUserId}
                />
            )}
        </div>
    );
}

// --- Property Card ---
function PropertyCard({ property, isEditMode, onEdit, onDelete, onView, effectiveUserId }) {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const imageMedia = property.media?.filter(m => m.type.startsWith('image')) || [];

    // Auto-slide effect
    useEffect(() => {
        if (imageMedia.length <= 1) return;
        
        const timer = setInterval(() => {
            setCurrentImageIndex(prev => 
                prev === imageMedia.length - 1 ? 0 : prev + 1
            );
        }, 3000);

        return () => clearInterval(timer);
    }, [imageMedia.length]);

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            if (imageMedia.length <= 1) return;
            setCurrentImageIndex(prev => 
                prev === imageMedia.length - 1 ? 0 : prev + 1
            );
        },
        onSwipedRight: () => {
            if (imageMedia.length <= 1) return;
            setCurrentImageIndex(prev => 
                prev === 0 ? imageMedia.length - 1 : prev - 1
            );
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    const mainImage = imageMedia.length > 0 ? imageMedia[currentImageIndex].url : null;
    const isExclusive = property.exclusivity === 'כן';

    return (
        <>
            <div 
                className={`bg-gray-800 rounded-lg shadow-lg overflow-hidden relative text-white flex flex-col md:flex-row transition-transform duration-200 ${!isEditMode && 'hover:scale-105 cursor-pointer'}`}
                onClick={!isEditMode ? onView : undefined}
            >
                {/* Card Content - Right Side */}
                <div className="p-4 flex-grow md:w-7/12">
                    {/* סוג נכס ומספר חדרים */}
                    <div className="text-amber-400 text-[2.125rem] font-bold">
                        {property.propertyType} | {property.rooms} חדרים
                    </div>

                    {/* רחוב ושכונה */}
                    <div className="text-white text-[1.125rem] mb-2">
                        {property.address?.street}
                        {property.address?.neighborhood && (
                            <> | {property.address.neighborhood}</>
                        )}
                    </div>

                    {/* מחיר */}
                    <div className="text-green-400 text-[1.5rem] font-bold">
                        ₪{property.price?.toString()}
                    </div>

                    {/* מאפיינים בסיסיים */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm my-2">
                        <span className="flex items-center gap-1"><BedIcon /> {property.rooms} חדרים</span>
                        <span className="flex items-center gap-1"><BathIcon /> {property.bathrooms} שירותים</span>
                        <span className="flex items-center gap-1"><RulerIcon /> {property.size} מ"ר</span>
                        {property.floor && <span className="flex items-center gap-1">קומה {formatFloor(property.floor)}</span>}
                    </div>

                    {/* תכונות נוספות */}
                    <div className="border-t border-gray-700 pt-3 mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                        {property.features?.parking > 0 && (
                            <span className="flex items-center gap-1">
                                <ParkingIcon />
                                {property.features.parking > 1 ? `${property.features.parking} חניות` : 'חניה'}
                            </span>
                        )}
                        {property.features?.elevator && <span className="flex items-center gap-1"><ElevatorIcon /> מעלית</span>}
                        {property.balcony > 0 && <span className="flex items-center gap-1"><BalconyIcon /> מרפסת</span>}
                        {property.features?.mamad && <span className="flex items-center gap-1"><MamadIcon /> ממ"ד</span>}
                        {property.features?.storage && <span className="flex items-center gap-1"><StorageIcon /> מחסן</span>}
                    </div>

                    {/* כפתורי עריכה ומחיקה */}
                    {isEditMode && (
                        <div className="mt-auto pt-4 flex gap-2">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(property);
                                }} 
                                className="bg-amber-500 text-black p-2 rounded-full hover:bg-amber-600 transition-all active:scale-95 active:translate-y-0.5" 
                                title="ערוך נכס"
                            >
                                <PencilIcon className="w-5 h-5" />
                            </button>
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(property);
                                }} 
                                className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all active:scale-95 active:translate-y-0.5" 
                                title="מחק נכס"
                            >
                                <TrashIcon className="w-5 h-5" />
                            </button>
                        </div>
                    )}

                    {/* כפתורי שיתוף */}
                    {!isEditMode && (
                        <div className="mt-auto pt-4 flex gap-2">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsShareDialogOpen(true);
                                }} 
                                className="bg-green-600 text-white p-2 rounded-full hover:bg-green-700 transition-all active:scale-95 active:translate-y-0.5" 
                                title="שתף בווטסאפ"
                            >
                                <WhatsAppIcon />
                            </button>
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    shareToFacebook(property);
                                }} 
                                className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-all active:scale-95 active:translate-y-0.5" 
                                title="שתף בפייסבוק"
                            >
                                <FacebookIcon />
                            </button>
                        </div>
                    )}
                </div>

                {/* Card Media - Left Side */}
                <div className="relative md:w-5/12 flex-shrink-0">
                    {isExclusive && (
                        <div className="absolute top-0 right-0 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg z-10 flex items-center">
                            <DiamondIcon />
                            <span>בבלעדיות</span>
                        </div>
                    )}
                    <div className="w-full h-48 md:h-full bg-gray-700 flex items-center justify-center overflow-hidden">
                        {mainImage ? (
                            <div {...handlers} className="w-full h-full">
                                <img src={mainImage} alt="תמונת נכס" className="w-full h-full object-cover" />
                            </div>
                        ) : (
                            <HouseIcon />
                        )}
                    </div>

                    {/* Image Navigation Dots */}
                    {imageMedia.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                            {imageMedia.map((_, index) => (
                                <button
                                    key={index}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        index === currentImageIndex ? 'bg-white' : 'bg-gray-400'
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentImageIndex(index);
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Share button */}
                    <div className="absolute top-2 left-2 flex gap-2 z-10">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsPreviewOpen(true);
                            }}
                            className="p-2 bg-gray-600 rounded-full text-white hover:bg-gray-700 transition-colors"
                            title="תצוגה מקדימה ושיתוף"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <SharePreviewDialog
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                property={property}
                onShare={() => setIsPreviewOpen(false)}
            />

            {/* Share Preview Dialog */}
            <SharePreviewDialog
                isOpen={isShareDialogOpen}
                onClose={() => setIsShareDialogOpen(false)}
                property={property}
                onShare={() => setIsShareDialogOpen(false)}
                effectiveUserId={effectiveUserId}
            />
        </>
    );
}

// --- Property Form Modal ---
function PropertyForm({ isOpen, onClose, onSave, initialData, effectiveUserId }) {
    const defaultForm = {
        propertyType: '',
        neighborhood: '',
        address: {
            street: '',
            number: '',
            city: 'שוהם'
        },
        rooms: '',
        floor: '',
        balcony: '',
        toilets: '',
        bathrooms: '',
        airConditioning: '',
        exclusivity: '',
        features: {
            parking: 0,
            elevator: false,
            mamad: false,
            storage: false
        },
        price: '',
        size: '',
        notes: '',
        media: [],
    };
    
    const neighborhoods = ['אבני חן', 'החורש', 'בצוותא', 'מעוף', 'הפרחים'];
    
    const [formData, setFormData] = useState(initialData ? { ...defaultForm, ...initialData } : defaultForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");

    const handleChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleFeatureToggle = (feature) => {
        setFormData(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [feature]: !prev.features[feature]
            }
        }));
    };

    const handleMediaUpdate = (updater) => {
        setFormData(prev => ({
            ...prev,
            media: updater(prev.media || [])
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setLoadingMessage("שומר שינויים...");
        try {
            await onSave(formData);
            await new Promise(resolve => setTimeout(resolve, 1000)); // להצגת האנימציה
        } finally {
            setIsSubmitting(false);
            setLoadingMessage("");
        }
    };

    const handleCancel = async () => {
        if (isSubmitting) return; // Prevent closing while submitting
        setIsSubmitting(true);
        setLoadingMessage("מבטל שינויים...");
        await new Promise(resolve => setTimeout(resolve, 1000)); // להצגת האנימציה
        setIsSubmitting(false);
        setLoadingMessage("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {isSubmitting && <LoadingAnimation message={loadingMessage} />}
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-white">{initialData ? 'עריכת נכס' : 'הוספת נכס חדש'}</h2>
                    <button 
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ✕
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4 text-right">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">סוג הנכס</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.propertyType}
                                onChange={(e) => handleChange('propertyType', e.target.value)}
                                required
                            >
                                <option value="">בחר סוג נכס</option>
                                {['דירה', 'דירת גן', 'גג/פנטהוז', 'דופלקס', 'קוטג\'', 'חנות'].map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">שכונה</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.neighborhood}
                                onChange={(e) => handleChange('neighborhood', e.target.value)}
                            >
                                <option value="">בחר שכונה</option>
                                {neighborhoods.map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">רחוב</label>
                            <input
                                type="text"
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.address?.street}
                                onChange={(e) => handleChange('address.street', e.target.value)}
                                placeholder="הכנס שם רחוב"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">מספר בית</label>
                            <input
                                type="text"
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.address?.number}
                                onChange={(e) => handleChange('address.number', e.target.value)}
                                placeholder="הכנס מספר בית"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">מחיר</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.price}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    
                                    // Remove any non-numeric characters except decimal point and commas
                                    value = value.replace(/[^\d.,]/g, '');
                                    // Remove commas for processing
                                    const cleanValue = value.replace(/,/g, '');
                                    
                                    // Ensure only one decimal point
                                    const decimalCount = (cleanValue.match(/\./g) || []).length;
                                    if (decimalCount > 1) {
                                        const parts = cleanValue.split('.');
                                        value = parts[0] + '.' + parts.slice(1).join('');
                                    }

                                    // Check if we have exactly 2 digits after decimal point
                                    if (cleanValue.includes('.')) {
                                        const [whole, decimal] = cleanValue.split('.');
                                        if (decimal && decimal.length === 2) {
                                            // Convert to full number format with commas
                                            const millionValue = parseFloat(cleanValue) * 1000000;
                                            const formattedValue = millionValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                            handleChange('price', formattedValue);
                                            return;
                                        }
                                    }
                                    // Otherwise just update the value as is
                                    handleChange('price', value);
                                }}
                                onBlur={(e) => {
                                    let value = e.target.value;
                                    // Remove commas for processing
                                    value = value.replace(/,/g, '');
                                    
                                    if (value) {
                                        if (value.includes('.')) {
                                            // Convert to full number format with commas
                                            const millionValue = parseFloat(value) * 1000000;
                                            const formattedValue = millionValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                            handleChange('price', formattedValue);
                                        } else {
                                            // If the number is large (not in millions format), leave it as is
                                            const numValue = parseFloat(value);
                                            if (!isNaN(numValue) && numValue < 1000) {
                                                // Small number without decimal - assume millions and format with commas
                                                const millionValue = numValue * 1000000;
                                                const formattedValue = millionValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                                handleChange('price', formattedValue);
                                            } else if (numValue >= 1000) {
                                                // Large number - just format with commas
                                                const formattedValue = numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                                handleChange('price', formattedValue);
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">שטח (מ"ר)</label>
                            <input
                                type="number"
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.size}
                                onChange={(e) => handleChange('size', e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">חדרים</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.rooms}
                                onChange={(e) => handleChange('rooms', e.target.value)}
                                required
                            >
                                <option value="">בחר מספר חדרים</option>
                                {[1, 2, 3, 4, 5, 6, 7].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">קומה</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.floor}
                                onChange={(e) => handleChange('floor', e.target.value)}
                            >
                                <option value="">בחר קומה</option>
                                <option value="מרתף">מרתף</option>
                                {[...Array(16)].map((_, i) => i - 5).map(num => (
                                    <option key={num} value={num}>{formatFloor(num)}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">מרפסות</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.balcony}
                                onChange={(e) => handleChange('balcony', e.target.value)}
                            >
                                <option value="">בחר מספר מרפסות</option>
                                {[0, 1, 2, 3].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">שירותים</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.toilets}
                                onChange={(e) => handleChange('toilets', e.target.value)}
                            >
                                <option value="">בחר מספר שירותים</option>
                                {[1, 2, 3].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">אמבטיות</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.bathrooms}
                                onChange={(e) => handleChange('bathrooms', e.target.value)}
                            >
                                <option value="">בחר מספר אמבטיות</option>
                                {[1, 2, 3].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">מיזוג</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.airConditioning}
                                onChange={(e) => handleChange('airConditioning', e.target.value)}
                            >
                                <option value="">בחר סוג מיזוג</option>
                                {['אין', 'מזגן בכל חדר', 'מיני מרכזי'].map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">בלעדיות</label>
                            <select
                                className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={formData.exclusivity}
                                onChange={(e) => handleChange('exclusivity', e.target.value)}
                            >
                                <option value="">בחר סטטוס בלעדיות</option>
                                {['כן', 'לא', 'שת"פ'].map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-300">חניה:</label>
                            <select
                                value={formData.features.parking}
                                onChange={(e) => handleChange('features.parking', Number(e.target.value))}
                                className="bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                            >
                                <option value="0">אין</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleFeatureToggle('elevator')}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                formData.features.elevator
                                ? 'bg-amber-500 text-black'
                                : 'bg-gray-700 text-white hover:bg-gray-600'
                            }`}
                        >
                            מעלית
                        </button>
                        <button
                            type="button"
                            onClick={() => handleFeatureToggle('mamad')}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                formData.features.mamad
                                ? 'bg-amber-500 text-black'
                                : 'bg-gray-700 text-white hover:bg-gray-600'
                            }`}
                        >
                            ממ"ד
                        </button>
                        <button
                            type="button"
                            onClick={() => handleFeatureToggle('storage')}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                formData.features.storage
                                ? 'bg-amber-500 text-black'
                                : 'bg-gray-700 text-white hover:bg-gray-600'
                            }`}
                        >
                            מחסן
                        </button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">הערות</label>
                        <textarea
                            className="w-full bg-gray-700 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            rows={4}
                        />
                    </div>

                    <MediaUploader 
                        effectiveUserId={effectiveUserId}
                        propertyId={initialData?.id || 'new'}
                        media={formData.media}
                        onMediaUpdate={handleMediaUpdate}
                    />

                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors disabled:opacity-50"
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-amber-500 text-black rounded hover:bg-amber-400 transition-colors disabled:opacity-50"
                        >
                            {initialData ? 'שמור שינויים' : 'הוסף נכס'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- Media Uploader Component ---
function MediaUploader({ effectiveUserId, propertyId, media, onMediaUpdate }) {
    const [isUploading, setIsUploading] = useState(false);
    
    const currentMedia = Array.isArray(media) ? media : [];

    const [uploadProgress, setUploadProgress] = useState({});

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            for (const file of files) {
                const fileId = Date.now().toString();
                const filePath = `users/${effectiveUserId}/properties/${propertyId}/${fileId}_${file.name}`;
                const fileRef = ref(storage, filePath);
                
                // יצירת placeholder בשביל התמונה
                onMediaUpdate(prevMedia => [...prevMedia, { 
                    id: fileId,
                    url: null, 
                    path: filePath, 
                    type: file.type,
                    isUploading: true
                }]);

                try {
                    const uploadTask = uploadBytesResumable(fileRef, file);

                    // מעקב אחר התקדמות ההעלאה
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(prev => ({
                                ...prev,
                                [fileId]: progress
                            }));
                        },
                        (error) => {
                            console.error("Upload failed:", error);
                            alert("שגיאה בהעלאת הקובץ");
                            onMediaUpdate(prevMedia => prevMedia.filter(item => item.id !== fileId));
                        },
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            onMediaUpdate(prevMedia => 
                                prevMedia.map(item => 
                                    item.id === fileId 
                                        ? { 
                                            url: downloadURL, 
                                            path: filePath, 
                                            type: file.type,
                                            id: fileId,
                                            isUploading: false
                                        }
                                        : item
                                )
                            );
                            setUploadProgress(prev => {
                                const { [fileId]: removed, ...rest } = prev;
                                return rest;
                            });
                        }
                    );
                } catch (error) {
                    console.error("Upload failed:", error);
                    alert("שגיאה בהעלאת הקובץ");
                    onMediaUpdate(prevMedia => prevMedia.filter(item => item.id !== fileId));
                }
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteMedia = async (itemToDelete) => {
        if (!window.confirm("האם למחוק את המדיה?")) return;
        try {
            const fileRef = ref(storage, itemToDelete.path);
            await deleteObject(fileRef);
            onMediaUpdate(prevMedia => prevMedia.filter(item => item.path !== itemToDelete.path));
        } catch (error) {
            console.error("Error deleting media:", error);
            alert("שגיאה במחיקת המדיה.");
        }
    };

    return (
        <div>
            <label className="block mb-1 font-semibold">תמונות וסרטונים</label>
            <div className="p-4 border-2 border-dashed border-gray-600 rounded-lg">
                <div className="flex flex-col gap-4">
                    {/* כפתור להעלאת תמונות מהגלריה */}
                    <div>
                        <label htmlFor="gallery-upload" className="block w-full text-center bg-amber-50 text-amber-700 py-2 px-4 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                            בחר מהגלריה
                        </label>
                        <input 
                            id="gallery-upload"
                            type="file" 
                            accept="image/*,video/*" 
                            multiple 
                            onChange={handleFileChange} 
                            className="hidden"
                        />
                    </div>
                    
                    {/* כפתור לצילום תמונה/וידאו */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label htmlFor="camera-upload" className="block w-full text-center bg-amber-50 text-amber-700 py-2 px-4 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                                צלם תמונה
                            </label>
                            <input 
                                id="camera-upload"
                                type="file" 
                                accept="image/*" 
                                capture="environment"
                                onChange={handleFileChange} 
                                className="hidden"
                            />
                        </div>
                        <div>
                            <label htmlFor="video-upload" className="block w-full text-center bg-amber-50 text-amber-700 py-2 px-4 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors">
                                צלם וידאו
                            </label>
                            <input 
                                id="video-upload"
                                type="file" 
                                accept="video/*" 
                                capture="environment"
                                onChange={handleFileChange} 
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                    {currentMedia.map((item, index) => (
                        <div key={item.id || index} className="relative group">
                            {item.isUploading ? (
                                <div className="w-full h-24 bg-gray-700 rounded-md flex flex-col items-center justify-center">
                                    <div className="text-amber-400 mb-2">
                                        <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                    <div className="w-full bg-gray-600 h-1 rounded-full overflow-hidden">
                                        <div 
                                            className="bg-amber-400 h-full transition-all duration-300"
                                            style={{ width: `${uploadProgress[item.id] || 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {item.type.startsWith('image') ? (
                                        <img src={item.url} alt={`media ${index}`} className="w-full h-24 object-cover rounded-md" />
                                    ) : (
                                        <video src={item.url} className="w-full h-24 object-cover rounded-md" />
                                    )}
                                    <button 
                                        type="button" 
                                        onClick={() => handleDeleteMedia(item)} 
                                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {isUploading && <LoadingAnimation message="מעלה תמונות..." />}
        </div>
    );
}


// --- Helper Components for the Form ---
const InputField = ({ label, name, type = 'text', value, onChange, inputMode }) => {
    // Helper function to format number with commas
    const formatNumberWithCommas = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const handlePriceChange = (e) => {
        let value = e.target.value;
        
        // Remove any non-numeric characters except decimal point and commas
        value = value.replace(/[^\d.,]/g, '');
        // Remove commas for processing
        value = value.replace(/,/g, '');
        
        // Ensure only one decimal point
        const decimalCount = (value.match(/\./g) || []).length;
        if (decimalCount > 1) {
            const parts = value.split('.');
            value = parts[0] + '.' + parts.slice(1).join('');
        }

        if (name === 'price' || name.toLowerCase().includes('price')) {
            // Check if we have exactly 2 digits after decimal point
            if (value.includes('.')) {
                const [whole, decimal] = value.split('.');
                if (decimal && decimal.length === 2) {
                    // Convert to full number format with commas
                    const millionValue = parseFloat(value) * 1000000;
                    const formattedValue = formatNumberWithCommas(millionValue);
                    onChange({ target: { name, value: formattedValue } });
                    return;
                }
            }
            // Otherwise just update the value as is
            onChange({ target: { name, value } });
        } else {
            onChange(e);
        }
    };

    const handlePriceBlur = (e) => {
        let value = e.target.value;
        // Remove commas for processing
        value = value.replace(/,/g, '');
        
        if ((name === 'price' || name.toLowerCase().includes('price')) && value) {
            if (value.includes('.')) {
                // Convert to full number format with commas
                const millionValue = parseFloat(value) * 1000000;
                const formattedValue = formatNumberWithCommas(millionValue);
                onChange({ target: { name, value: formattedValue } });
            } else {
                // If the number is large (not in millions format), leave it as is
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && numValue < 1000) {
                    // Small number without decimal - assume millions and format with commas
                    const millionValue = numValue * 1000000;
                    const formattedValue = formatNumberWithCommas(millionValue);
                    onChange({ target: { name, value: formattedValue } });
                } else if (numValue >= 1000) {
                    // Large number - just format with commas
                    const formattedValue = formatNumberWithCommas(numValue);
                    onChange({ target: { name, value: formattedValue } });
                }
            }
        }
    };

    return (
        <div>
            <label htmlFor={name} className="block mb-1 font-semibold">{label}</label>
            {type === 'textarea' ? (
                <textarea 
                    id={name} 
                    name={name} 
                    value={value || ''} 
                    onChange={onChange} 
                    className="bg-gray-700 p-2 rounded w-full h-24" 
                />
            ) : (
                <input 
                    id={name} 
                    name={name} 
                    type={type} 
                    value={value || ''} 
                    onChange={name === 'price' ? handlePriceChange : onChange}
                    onBlur={name === 'price' ? handlePriceBlur : undefined}
                    className="bg-gray-700 p-2 rounded w-full" 
                    inputMode={inputMode || (type === 'number' ? 'decimal' : 'text')}
                />
            )}
        </div>
    );
};

const SelectField = ({ label, name, value, onChange, options }) => (
    <div>
        <label htmlFor={name} className="block mb-1 font-semibold">{label}</label>
        <select id={name} name={name} value={value || ''} onChange={onChange} className="bg-gray-700 p-2 rounded w-full">
            <option value="">בחר...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const ToggleButton = ({ name, checked, onChange, children }) => (
    <button type="button" onClick={() => onChange(name)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${checked ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-200'}`}>
        {children}
    </button>
);

// --- Filter Component (Placeholder) ---
function FilterComponent({ filters, setFilters }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Helper function to format number with commas for filters
    const formatNumberWithCommas = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const handleFilterChange = (field, value) => {
        if (field.startsWith('priceRange.')) {
            const [parent, child] = field.split('.');
            
            // Remove any non-numeric characters except decimal point and commas
            let newValue = value.replace(/[^\d.,]/g, '');
            // Remove commas for processing
            newValue = newValue.replace(/,/g, '');
            
            // Ensure only one decimal point
            const decimalCount = (newValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
                const parts = newValue.split('.');
                newValue = parts[0] + '.' + parts.slice(1).join('');
            }

            // Check if we have exactly 2 digits after decimal point
            if (newValue.includes('.')) {
                const [whole, decimal] = newValue.split('.');
                if (decimal && decimal.length === 2) {
                    // Convert to full number format with commas
                    const millionValue = parseFloat(newValue) * 1000000;
                    newValue = formatNumberWithCommas(millionValue);
                }
            }

            setFilters(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: newValue }
            }));
        } else if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFilters(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setFilters(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleFeaturesChange = (feature) => {
        setFilters(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [feature]: !prev.features[feature]
            }
        }));
    };

    const handlePriceBlur = (field, value) => {
        if (field.startsWith('priceRange.')) {
            const [parent, child] = field.split('.');
            let newValue = value.replace(/,/g, ''); // Remove commas for processing
            
            if (newValue && newValue.includes('.')) {
                // Convert to full number format with commas
                const millionValue = parseFloat(newValue) * 1000000;
                newValue = formatNumberWithCommas(millionValue);
            } else if (newValue) {
                // If it's a small number without decimal, check if we should convert
                const numValue = parseFloat(newValue);
                if (!isNaN(numValue) && numValue < 1000) {
                    // Small number - assume millions
                    newValue = formatNumberWithCommas(numValue * 1000000);
                } else if (numValue >= 1000) {
                    // Large number - just format with commas
                    newValue = formatNumberWithCommas(numValue);
                }
            }

            setFilters(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: newValue }
            }));
        }
    };

    const neighborhoods = ['הכל', 'אבני חן', 'החורש', 'בצוותא', 'מעוף', 'הפרחים'];

    return (
        <div className="bg-gray-800 rounded-lg p-4 mb-6 text-white">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between text-amber-400 hover:text-amber-300 transition-colors"
            >
                <span className="text-lg font-semibold">סינון ומיון</span>
                <svg
                    className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isExpanded && (
                <div className="mt-4 space-y-6">
                    {/* סינונים ראשיים */}
                    <div className="flex flex-col lg:flex-row gap-4 flex-wrap">
                        {/* סוג נכס */}
                        <div className="bg-gray-700/50 p-3 rounded-lg flex-1">
                            <label className="text-gray-300 block mb-2 text-sm">סוג הנכס</label>
                            <select
                                className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={filters.propertyType}
                                onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                            >
                                <option value="">הכל</option>
                                {['דירה', 'דירת גן', 'גג/פנטהוז', 'דופלקס', 'קוטג\'', 'חנות'].map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>

                        {/* שכונה */}
                        <div className="bg-gray-700/50 p-3 rounded-lg flex-1">
                            <label className="text-gray-300 block mb-2 text-sm">שכונה</label>
                            <select
                                className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={filters.neighborhood}
                                onChange={(e) => handleFilterChange('neighborhood', e.target.value)}
                            >
                                {neighborhoods.map(n => (
                                    <option key={n} value={n === 'הכל' ? '' : n}>{n}</option>
                                ))}
                            </select>
                        </div>

                        {/* חדרים */}
                        <div className="bg-gray-700/50 p-3 rounded-lg" style={{ width: '120px' }}>
                            <label className="text-gray-300 block mb-2 text-sm">חדרים</label>
                            <select
                                className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={filters.rooms}
                                onChange={(e) => handleFilterChange('rooms', e.target.value)}
                            >
                                <option value="">הכל</option>
                                {[1, 2, 3, 4, 5, 6, 7].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>

                        {/* בלעדיות */}
                        <div className="bg-gray-700/50 p-3 rounded-lg flex-1">
                            <label className="text-gray-300 block mb-2 text-sm">בלעדיות</label>
                            <select
                                className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                value={filters.exclusivity}
                                onChange={(e) => handleFilterChange('exclusivity', e.target.value)}
                            >
                                <option value="">הכל</option>
                                <option value="כן">בלעדיות</option>
                                <option value="שת&quot;פ">שת"פ</option>
                                <option value="לא">ללא בלעדיות</option>
                            </select>
                        </div>

                        {/* טווח מחירים */}
                        <div className="bg-gray-700/50 p-3 rounded-lg" style={{ width: '200px' }}>
                            <label className="text-gray-300 block mb-2 text-sm">טווח מחירים</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="מ-"
                                    className="w-1/2 bg-gray-600 text-white rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.priceRange.min}
                                    onChange={(e) => handleFilterChange('priceRange.min', e.target.value)}
                                    onBlur={(e) => handlePriceBlur('priceRange.min', e.target.value)}
                                />
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="עד-"
                                    className="w-1/2 bg-gray-600 text-white rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.priceRange.max}
                                    onChange={(e) => handleFilterChange('priceRange.max', e.target.value)}
                                    onBlur={(e) => handlePriceBlur('priceRange.max', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* כפתור סינונים נוספים */}
                    <div className="flex justify-center">
                        <button
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
                        >
                            <span>{showAdvancedFilters ? 'הסתר' : 'הצג'} סינונים נוספים</span>
                            <svg
                                className={`w-5 h-5 transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>

                    {/* סינונים נוספים */}
                    {showAdvancedFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-700">
                            {/* קומה */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">קומה</label>
                                <select
                                    className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.floor}
                                    onChange={(e) => handleFilterChange('floor', e.target.value)}
                                >
                                    <option value="">הכל</option>
                                    <option value="מרתף">מרתף</option>
                                    {[...Array(16)].map((_, i) => i - 5).map(num => (
                                        <option key={num} value={num}>{formatFloor(num)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* מרפסות */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">מרפסות</label>
                                <select
                                    className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.balcony}
                                    onChange={(e) => handleFilterChange('balcony', e.target.value)}
                                >
                                    <option value="">הכל</option>
                                    {[1, 2, 3].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>

                            {/* שירותים */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">שירותים</label>
                                <select
                                    className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.toilets}
                                    onChange={(e) => handleFilterChange('toilets', e.target.value)}
                                >
                                    <option value="">הכל</option>
                                    {[1, 2, 3].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>

                            {/* אמבטיות */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">אמבטיות</label>
                                <select
                                    className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.bathrooms}
                                    onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                                >
                                    <option value="">הכל</option>
                                    {[1, 2, 3].map(num => (
                                        <option key={num} value={num}>{num}</option>
                                    ))}
                                </select>
                            </div>

                            {/* מיזוג */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">מיזוג</label>
                                <select
                                    className="w-full bg-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    value={filters.airConditioning}
                                    onChange={(e) => handleFilterChange('airConditioning', e.target.value)}
                                >
                                    <option value="">הכל</option>
                                    <option value="אין">אין</option>
                                    <option value="מזגן בכל חדר">מזגן בכל חדר</option>
                                    <option value="מיני מרכזי">מיני מרכזי</option>
                                </select>
                            </div>

                            {/* מאפיינים נוספים */}
                            <div className="bg-gray-700/50 p-3 rounded-lg">
                                <label className="text-gray-300 block mb-2 text-sm">מאפיינים נוספים</label>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm">חניה:</label>
                                        <select
                                            value={filters.features.parking ? "1" : "0"}
                                            onChange={(e) => handleFeaturesChange('parking', e.target.value === "1")}
                                            className="bg-gray-600 text-white rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                        >
                                            <option value="0">הכל</option>
                                            <option value="1">יש</option>
                                        </select>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filters.features.elevator}
                                            onChange={() => handleFeaturesChange('elevator')}
                                            className="rounded text-amber-400 focus:ring-amber-400"
                                        />
                                        <span>מעלית</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filters.features.mamad}
                                            onChange={() => handleFeaturesChange('mamad')}
                                            className="rounded text-amber-400 focus:ring-amber-400"
                                        />
                                        <span>ממ"ד</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filters.features.storage}
                                            onChange={() => handleFeaturesChange('storage')}
                                            className="rounded text-amber-400 focus:ring-amber-400"
                                        />
                                        <span>מחסן</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* כפתור איפוס */}
                    <div className="flex justify-end">
                        <button
                            onClick={() => setFilters({
                                priceRange: { min: '', max: '' },
                                rooms: '',
                                floor: '',
                                propertyType: '',
                                balcony: '',
                                toilets: '',
                                bathrooms: '',
                                airConditioning: '',
                                exclusivity: '',
                                features: { parking: false, elevator: false, mamad: false, storage: false },
                                sizeRange: { min: '', max: '' },
                                city: '',
                                searchText: '',
                                neighborhood: ''
                            })}
                            className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-500 transition-colors"
                        >
                            נקה הכל
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Property Detail Modal ---
// --- New Component for Image Gallery ---
function ImageGallery({ media }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const imageMedia = Array.isArray(media) ? media.filter(m => m.type.startsWith('image')) : [];

    if (imageMedia.length === 0) {
        return <div className="w-full h-full bg-gray-700 flex items-center justify-center text-gray-400 rounded-lg">אין תמונות להצגה</div>;
    }

    const goToPrevious = () => {
        const isFirst = currentIndex === 0;
        setCurrentIndex(isFirst ? imageMedia.length - 1 : currentIndex - 1);
    };

    const goToNext = () => {
        const isLast = currentIndex === imageMedia.length - 1;
        setCurrentIndex(isLast ? 0 : currentIndex + 1);
    };

    const handlers = useSwipeable({
        onSwipedLeft: () => goToNext(),
        onSwipedRight: () => goToPrevious(),
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    return (
        <div className="w-full h-full relative">
            <div {...handlers} className="w-full h-full rounded-lg overflow-hidden">
                <img src={imageMedia[currentIndex].url} alt="תמונת נכס" className="w-full h-full object-cover"/>
            </div>
            {imageMedia.length > 1 && (
                <>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            goToPrevious();
                        }} 
                        className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-10 hover:bg-opacity-75 transition-all"
                        aria-label="תמונה קודמת"
                    >
                        &#10095;
                    </button>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            goToNext();
                        }} 
                        className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-10 hover:bg-opacity-75 transition-all"
                        aria-label="תמונה הבאה"
                    >
                        &#10094;
                    </button>
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                        {imageMedia.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all ${
                                    index === currentIndex 
                                    ? 'bg-white' 
                                    : 'bg-white bg-opacity-50'
                                }`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
// --- New Component for Inline Property Details ---
function PropertyDetails({ property }) {
    return (
        <div className="bg-gray-800 rounded-lg p-6 text-white">
            {/* כותרת ראשית */}
            <div className="text-amber-400 text-[2.125rem] font-bold mb-2">
                {property.propertyType} | {property.rooms} חדרים
            </div>

            {/* כתובת */}
            <div className="text-white text-[1.125rem] mb-2">
                {property.address?.street}
                {property.neighborhood && (
                    <> | {property.neighborhood}</>
                )}
            </div>

            {/* מחיר */}
            <div className="text-2xl text-[1.5rem] font-light mb-6">
                ₪{property.price?.toString()}
            </div>

            {/* שאר הפרטים */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-xl font-semibold border-b border-gray-700 pb-2 mb-3">פרטי הנכס</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <DetailItem label="גודל" value={`${property.size} מ"ר`} />
                        <DetailItem label="קומה" value={formatFloor(property.floor)} />
                        <DetailItem label="שירותים" value={property.toilets} />
                        <DetailItem label="אמבטיות" value={property.bathrooms} />
                        <DetailItem label="מרפסות" value={property.balcony} />
                    </div>
                </div>
                <div>
                    <h3 className="text-xl font-semibold border-b border-gray-700 pb-2 mb-3">מאפיינים</h3>
                    <div className="space-y-2">
                        <FeatureItem icon={<ParkingIcon />} text="חניה" value={property.features?.parking > 0 ? (property.features.parking > 1 ? `${property.features.parking} חניות` : 'חניה') : null} />
                        <FeatureItem icon={<ElevatorIcon />} text="מעלית" available={property.features?.elevator} />
                        <FeatureItem icon={<MamadIcon />} text='ממ"ד' available={property.features?.mamad} />
                        <FeatureItem icon={<StorageIcon />} text="מחסן" available={property.features?.storage} />
                        <FeatureItem icon={<AirConIcon />} text="מיזוג" value={property.airConditioning} />
                        <FeatureItem icon={<DiamondIcon />} text="בלעדיות" value={property.exclusivity} />
                    </div>
                </div>
            </div>

            {property.notes && (
                <div className="mt-6">
                    <h3 className="text-xl font-semibold border-b border-gray-700 pb-2 mb-3">הערות</h3>
                    <p className="text-gray-300 whitespace-pre-wrap">{property.notes}</p>
                </div>
            )}
        </div>
    );
}

const DetailItem = ({ label, value }) => (
    <div>
        <p className="text-sm text-gray-400">{label}</p>
        <p className="text-lg font-medium">{value}</p>
    </div>
);

const FeatureItem = ({ icon, text, available, value }) => {
    // אם זה מאפיין בוליאני (כמו מעלית או ממ"ד) ואין אותו - לא להציג
    if (typeof available === 'boolean' && !available) return null;
    
    // אם זה מאפיין עם ערך (כמו חניה או מיזוג) ואין ערך או הערך הוא 0 - לא להציג
    if (value !== undefined) {
        if (!value || value === '0' || value === 0 || value === 'אין') return null;
    }
    
    // אם הגענו לכאן, יש לנו מאפיין להציג
    return (
        <div className="flex items-center gap-2 text-gray-300">
            {icon}
            <span>{text}</span>
            {value && value !== 'כן' && <span className="text-amber-400">: {value}</span>}
        </div>
    );
};

// פונקציית עזר לפורמוט קומה
const formatFloor = (floor) => {
    if (!floor && floor !== 0) return '';
    if (floor === 'מרתף') return 'מרתף';
    const floorNum = parseInt(floor);
    if (floorNum < 0) {
        return `${Math.abs(floorNum)}-`;
    }
    return floorNum.toString();
};

// Add utility function to get the first image URL from property
const getPropertyMainImage = (property) => {
    if (property.media && property.media.length > 0) {
        return property.media[0].url;
    }
    return null;
};

// Add SharePreviewDialog component
const SharePreviewDialog = ({ isOpen, onClose, property, onShare, effectiveUserId }) => {
    const [editedText, setEditedText] = useState('');
    const [shareType, setShareType] = useState(null);
    const mainImage = getPropertyMainImage(property);

    useEffect(() => {
        if (isOpen) {
            setEditedText(formatPropertyText(property));
        }
    }, [isOpen, property]);

    const handleShare = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (shareType === 'whatsapp') {
            shareToWhatsApp(property, editedText);
        } else if (shareType === 'facebook') {
            shareToFacebook(property, editedText);
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-white">תצוגה מקדימה של המודעה</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                    >
                        ✕
                    </button>
                </div>

                {mainImage && (
                    <div className="mb-4">
                        <img src={mainImage} alt="תמונת הנכס" className="w-full h-64 object-cover rounded-lg"/>
                    </div>
                )}

                <div className="mb-4">
                    <textarea
                        value={editedText}
                        onChange={(e) => setEditedText(e.target.value)}
                        className="w-full h-64 p-3 bg-gray-700 text-white rounded-lg"
                        dir="rtl"
                    />
                </div>

                <div className="flex gap-4 justify-center">
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            shareToWhatsApp(property, editedText, effectiveUserId);
                            onClose();
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <WhatsAppIcon />
                        שתף בווטסאפ
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            shareToFacebook(property, editedText);
                            onClose();
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <FacebookIcon />
                        שתף בפייסבוק
                    </button>
                </div>
            </div>
        </div>
    );
};

// Update share functions to accept custom text
const getPublicUrl = (userId) => {
    return `https://yakov-7798e.firebaseapp.com/shop/${userId}`;
};

const shareToWhatsApp = (property, customText = null, userId) => {
    const text = customText || formatPropertyText(property);
    const url = `${getPublicUrl(userId)}?propertyId=${property.id}`;
    const encodedText = encodeURIComponent(`${text}\n\n${url}`);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
};

const shareToFacebook = async (property, customText = null) => {
    const url = `${getPublicUrl()}/nadlan?propertyId=${property.id}`;
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
};

// עדכון פונקציית פורמט הטקסט להוספת אימוג'י תמונה
const formatPropertyText = (property) => {
    const formatPrice = (price) => {
        if (!price) return '';
        // Remove commas first
        const cleanPrice = price.toString().replace(/,/g, '');
        const numPrice = parseFloat(cleanPrice);
        
        // If price is already in full number format, convert to millions
        if (numPrice >= 1000) {
            return (numPrice / 1000000).toFixed(2);
        }
        // If price is already in millions format (e.g., 1.5), return as is
        return numPrice.toFixed(2);
    };

    let text = '';
    
    // Add exclusivity banner if property is exclusive
    if (property.exclusivity === 'כן') {
        text += '💎 בבלעדיות 💎\n\n';
    }

    // Basic property details
    text += `🏢 ${property.propertyType} ${property.rooms} חדרים\n`;
    if (property.address?.street) text += `📍 ${property.address.street}\n`;
    if (property.address?.city) text += `🌆 ${property.address.city}\n`;
    text += `💰 ${formatPrice(property.price)} מיליון ₪\n`;
    if (property.size) text += `📏 ${property.size} מ"ר\n`;
    text += `🏢 קומה ${property.floor}\n`;
    
    // Bathrooms and toilets
    const totalBathrooms = Number(property.bathrooms || 0) + Number(property.toilets || 0);
    if (totalBathrooms > 0) {
        text += `🚽 ${totalBathrooms} חדרי אמבטיה/שירותים\n`;
    }
    
    // Balconies
    if (property.balcony && property.balcony > 0) {
        text += `🌅 ${property.balcony} מרפסות\n`;
    }

    // Features section
    text += '\n✨ מאפיינים:\n';

    // Parking
    if (property.features?.parking > 0) {
        text += `🅿️ ${property.features.parking} חניות\n`;
    }
    
    // Other features
    if (property.features?.elevator) text += '🛗 מעלית\n';
    if (property.features?.mamad) text += '🚪 ממ"ד\n';
    if (property.features?.storage) text += '📦 מחסן\n';
    if (property.airConditioning === 'מזגן בכל חדר') text += '❄️ מזגן בכל חדר\n';

    return text.trim();
};